/**
 * صدای زنده سرِ میزِ اتاقِ دوستان — همتای VoiceClient ِ اپ.
 *
 * به هیچ بازی‌ای سیم‌کشی نشده: از سیگنال‌های میزِ [[client]] پیروی می‌کند.
 * میزی که در آن نشسته‌ام **یا تماشایش می‌کنم** پیدا شد → بلیت؛ میز رفت → بیرون.
 * این‌که این میز صدا دارد یا نه را سرور می‌گوید (فقط اتاقِ دوستان)، پس صفِ سریع
 * خودبه‌خود بی‌صدا می‌مانَد؛ تماشاچی بلیتِ فقط‌شنیدن می‌گیرد (can_speak=false)،
 * پس نه پرسشِ اجازه می‌بیند و نه میکروفونش باز می‌شود.
 *
 * **تماشاچی فقط می‌شنود — این‌جا هم، نه فقط در بلیت (اکتبر ۲۰۲۶).** اپ با همان بلیت
 * پنجره‌ی اجازه‌ی میکروفون را به تماشاچی نشان می‌داد؛ این‌جا `listenOnly` (از `watching` ِ
 * خودِ وضعیتِ میز) هر دروازه‌ی میکروفون را می‌بندد: نه پرسش، نه getUserMedia، نه
 * انتشار — حتی اگر رویدادِ مجوزِ LiveKit یا دکمه‌ای چیزِ دیگری بگوید.
 *
 * **صدای پیوسته با اجازه‌ی کاربر (سپتامبر ۲۰۲۶).** بارِ اول که میزی صدا دارد
 * [[VoiceButton]] می‌پرسد (`ask`) و پاسخ در localStorage می‌مانَد. با «بله»
 * میکروفون تا آخرِ میز باز است — نه نگه‌دار و حرف بزن — و صدای دیگران همیشه
 * پخش می‌شود. تنها کنترل `toggleMute()` است: دکمه‌ی گردِ پایین-چپِ میز. بی‌صدا
 * یعنی تراک بسته (stopMicTrackOnMute)، تا نشانگرِ میکروفونِ مرورگر هم خاموش شود.
 * مرورگر اجازه نداد: دکمه بی‌صدا می‌مانَد و لمسش توضیح می‌دهد و دوباره می‌کوشد.
 *
 * **بستنِ پنل.** سرور می‌تواند حرف‌زدنِ کسی را ببندد (canPublish)؛ از رویدادِ
 * مجوزِ خودِ LiveKit باخبر می‌شویم (`canSpeak`). کنترلِ میزبان روی میز دیگر
 * نیست — تنها دکمه همان بی‌صداست.
 *
 * **نوبتِ مافیا (اکتبر ۲۰۲۶).** سرِ میزِ مافیا خودِ بازی نوبتِ حرف می‌دهد: شب همه
 * ساکت، نوبتِ حرف فقط گوینده (در دفاع همان متهم)، رأی در سکوت، پایانِ بازی همه.
 * سرور روی LiveKit اجرایش می‌کند (همان `canSpeak`)؛ `turn` فقط می‌گوید **چرا**،
 * از روی همان MafiaState، تا دکمه «نوبتِ توست» و «بازی بسته» را از هم جدا کند.
 * پرسشِ اجازه همان اولِ میز می‌آید، نه وسطِ اولین نوبت که ثانیه‌هایش می‌رود.
 *
 * **livekit-client دیر بار می‌شود** (import ِ پویا)، فقط وقتی میز بلیت گرفت:
 * بسته‌ی /play بی آن ۸۴ و با آن ۲۳۳ کیلوبایتِ فشرده بود.
 *
 * مرورگرها پخشِ صدا را تا اولین لمس نگه می‌دارند (autoplay). `blocked` همین را
 * می‌گوید؛ هر لمسی روی صفحه بازش می‌کند.
 */
import type { Room, RemoteTrack, Participant } from "livekit-client";
import { client } from "./client";
import { signal } from "./signal";
import { blocked, isBlocked } from "./safety";
import type { MafiaState } from "../gen/bazivan/v1/table_pb";
import { StepKind } from "../mafia/text";

export type VoicePhase = "off" | "asking" | "connecting" | "live" | "none" | "failed";
/** پاسخِ کاربر به «صدای زنده؟» — در localStorage می‌مانَد. */
export type VoiceConsent = "unknown" | "yes" | "no";
/** نوبتِ صدا سرِ میزی که بازی نوبت می‌دهد (مافیا): "" = بازی کاری ندارد (میزِ دیگر، تماشا، پایانِ بازی). */
export type VoiceTurn = "" | "mine" | "night" | "other";

/** همان قاعده‌ی سرور (table/mafia_voice.go)، فقط برای نمایش: شب هیچ‌کس، نوبتِ حرف فقط گوینده. */
export function mafiaVoiceTurn(m: MafiaState | null): VoiceTurn {
  if (!m || m.watching || m.over) return "";
  if (m.night) return "night";
  return m.stepKind === StepKind.Talk && m.actor === m.me ? "mine" : "other";
}

const EMPTY: ReadonlySet<string> = new Set();

class Voice {
  readonly phase = signal<VoicePhase>("off");
  /** اجازه‌ی حرف‌زدن دارم؟ (پنل می‌بندد.) */
  readonly canSpeak = signal(false);
  /** هر کس — خودم هم — که همین حالا حرف می‌زند: حلقه‌ی دورِ آواتار. */
  readonly speaking = signal<ReadonlySet<string>>(EMPTY);
  /** کسانی که در اتاقِ صدا هستند: نشانِ میکروفون روی آواتارشان. */
  readonly members = signal<ReadonlySet<string>>(EMPTY);
  /** کسانی که میکروفونشان بسته است (بی‌صدا کرده، اجازه نداده، یا پنل بسته): نشانِ سرخ. */
  readonly silenced = signal<ReadonlySet<string>>(EMPTY);
  /** مرورگر هنوز اجازه‌ی پخش نداده — یک لمس لازم است. */
  readonly blocked = signal(false);
  /** پاسخِ ذخیره‌شده‌ی کاربر. */
  readonly consent = signal<VoiceConsent>(readConsent());
  /** خودم بی‌صدا کرده‌ام (با دکمه). در همین نشست می‌مانَد، میز به میز. */
  readonly muted = signal(false);
  /** میکروفونم همین حالا باز و منتشر است. */
  readonly micOn = signal(false);
  /** میکروفون دارد باز می‌شود (یا پنجره‌ی اجازه‌ی مرورگر باز است). */
  readonly opening = signal(false);
  /** مرورگر اجازه‌ی میکروفون را نداد (یا صفحه https نیست). */
  readonly denied = signal(false);
  /** همین حالا پرسشِ «صدای زنده؟» را نشان بده: میزِ صدادار و هنوز بی‌پاسخ. */
  readonly ask = signal(false);
  /** نوبتِ صدا سرِ میزِ مافیا — [[mafiaVoiceTurn]]. */
  readonly turn = signal<VoiceTurn>("");

  private table = "";
  /** تماشاچیِ همین میزم: فقط می‌شنوم. */
  private listenOnly = false;
  private gen = 0;
  private room: Room | null = null;
  private els = new Map<string, HTMLMediaElement>();
  private skipped = false;           // پرسش را بی پاسخ بست — سرِ همین میز دوباره نپرس
  private failed = false;            // میکروفون نیست/باز نشد — تا لمسِ بعدی دوباره نکوش
  private busy: Promise<void> = Promise.resolve();

  constructor() {
    const sync = () => { const t = atTable(); this.follow(t.id, t.watching); };
    for (const s of [client.table, client.cbTable, client.hkTable, client.shTable, client.board, client.mafia]) s.subscribe(sync);
    client.mafia.subscribe(() => {
      const t = mafiaVoiceTurn(client.mafia.value);
      if (t === this.turn.value) return;
      this.turn.value = t;
      this.refreshAsk();
    });
    // هر لمسی قفلِ پخشِ مرورگر را باز می‌کند — صدای دیگران همیشه پخش شود.
    addEventListener("pointerdown", () => this.unlock(), { capture: true });
    addEventListener("keydown", () => this.unlock(), { capture: true });
    void this.watchPermission();
  }

  // ─────────────────────────── میز ───────────────────────────

  /** وصل نشد (شبکه، سرورِ صدا): همان میز را از نو — از لمسِ دکمه‌ی خاکستری. */
  retry(): void {
    const t = this.table;
    if (!t || this.phase.value !== "failed") return;
    this.table = "";
    this.follow(t, this.listenOnly);
  }

  /** همان میز و همان نقش باشد، کاری نمی‌کند؛ خالی یعنی بیرون. `watching` = تماشاچی، فقط می‌شنود. */
  private follow(tableId: string, watching: boolean): void {
    if (tableId === this.table && watching === this.listenOnly) return;
    this.leave();
    if (!tableId) return;
    this.table = tableId;
    this.listenOnly = watching;
    const gen = ++this.gen;
    this.phase.value = "asking";
    void client.voiceTicket().then((t) => {
      if (gen !== this.gen) return;
      if (t.error === "not_in_room" || t.error === "voice_off") { this.phase.value = "none"; return; }
      if (t.error || !t.url || !t.token) { console.warn("voice: ticket", t.error); this.phase.value = "failed"; return; }
      void this.connect(t.url, t.token, !!t.can_speak, gen);
    });
  }

  private async connect(url: string, token: string, canSpeak: boolean, gen: number): Promise<void> {
    this.phase.value = "connecting";
    let lk: typeof import("livekit-client");
    try {
      lk = await import("livekit-client");
    } catch (e) {
      if (gen === this.gen) { console.warn("voice: sdk load failed", e); this.phase.value = "failed"; }
      return;
    }
    if (gen !== this.gen) return;
    const { RoomEvent } = lk;
    const room = new lk.Room({
      adaptiveStream: true,
      dynacast: true,
      // بی‌صدا = میکروفون بسته، نه فقط ساکت: نشانگرِ مرورگر هم خاموش.
      stopLocalTrackOnUnpublish: true,
      audioCaptureDefaults: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      publishDefaults: { audioPreset: { maxBitrate: 24_000 }, dtx: true, red: true, stopMicTrackOnMute: true },
    });
    const roster = () => this.roster(room);
    room
      .on(RoomEvent.TrackSubscribed, (track: RemoteTrack, _pub, who) => this.subscribed(track, who.identity))
      .on(RoomEvent.TrackUnsubscribed, (track: RemoteTrack) => this.unsubscribed(track))
      .on(RoomEvent.ActiveSpeakersChanged, (ps: Participant[]) => { this.speaking.value = new Set(ps.map((p) => p.identity)); })
      .on(RoomEvent.ParticipantConnected, roster)
      .on(RoomEvent.ParticipantDisconnected, roster)
      .on(RoomEvent.TrackPublished, roster)
      .on(RoomEvent.TrackUnpublished, roster)
      .on(RoomEvent.TrackMuted, roster)
      .on(RoomEvent.TrackUnmuted, roster)
      .on(RoomEvent.LocalTrackPublished, roster)
      .on(RoomEvent.LocalTrackUnpublished, roster)
      .on(RoomEvent.ParticipantPermissionsChanged, (_prev, who: Participant) => {
        if (who.identity === room.localParticipant.identity) {
          this.canSpeak.value = !this.listenOnly && !!room.localParticipant.permissions?.canPublish;
          this.refreshAsk();
          this.ensure();   // پنل بست: همان لحظه بسته؛ باز کرد: دوباره باز
        }
        roster();
      })
      .on(RoomEvent.AudioPlaybackStatusChanged, () => { this.blocked.value = !room.canPlaybackAudio; })
      .on(RoomEvent.Disconnected, () => { if (this.room === room) this.leave(); });
    try {
      await room.connect(url, token, { autoSubscribe: true });
    } catch (e) {
      if (gen === this.gen) { console.warn("voice: connect failed", e); this.phase.value = "failed"; }
      return;
    }
    if (gen !== this.gen) { void room.disconnect(); return; }
    this.room = room;
    this.canSpeak.value = canSpeak && !this.listenOnly;
    this.blocked.value = !room.canPlaybackAudio;
    roster();
    this.phase.value = "live";
    this.refreshAsk();
    this.ensure();   // پیش‌تر «بله» گفته: میکروفون همین حالا باز
    console.info("voice: live", room.name, "speak=" + canSpeak, "watching=" + this.listenOnly);
  }

  /** چه کسانی در اتاقِ صدا هستند و میکروفونِ کدام بسته است — از خودِ LiveKit. */
  private roster(room: Room): void {
    if (this.room && this.room !== room) return;
    const me = room.localParticipant;
    const members = new Set<string>([me.identity]);
    const silenced = new Set<string>();
    if (!me.isMicrophoneEnabled || (me.permissions && !me.permissions.canPublish)) silenced.add(me.identity);
    for (const p of room.remoteParticipants.values()) {
      members.add(p.identity);
      if (!p.isMicrophoneEnabled || (p.permissions && !p.permissions.canPublish)) silenced.add(p.identity);
    }
    this.members.value = members;
    this.silenced.value = silenced;
  }

  private subscribed(track: RemoteTrack, who: string): void {
    if (track.kind !== "audio" || !track.sid || this.els.has(track.sid)) return;
    const el = track.attach();
    el.dataset.voice = who;
    // بلاک‌شده: صدایش به تو نمی‌رسد (net/safety.ts). بقیه‌ی اتاق هنوز می‌شنوندش.
    el.muted = isBlocked(who);
    document.body.appendChild(el);
    this.els.set(track.sid, el);
  }

  /** بلاک عوض شد: عنصرهای صدای حاضر هم همین حالا. */
  applyBlocks(): void {
    for (const el of this.els.values()) el.muted = isBlocked(el.dataset.voice ?? "");
  }

  private unsubscribed(track: RemoteTrack): void {
    if (!track.sid) return;
    const el = this.els.get(track.sid);
    if (el) { track.detach(el); el.remove(); }
    this.els.delete(track.sid);
  }

  // ─────────────────────────── اجازه و بی‌صدا ───────────────────────────

  /** «اجازه می‌دهم» — یا لمسِ دکمه در حالتِ بی‌اجازه. پاسخ می‌مانَد و میکروفون باز می‌شود
   *  (مرورگر اگر لازم باشد خودش می‌پرسد). از دستِ کاربر صدا زده شود: قفلِ پخش هم باز می‌شود. */
  accept(): void {
    if (this.listenOnly) return;   // تماشاچی: نه پاسخی می‌مانَد، نه پنجره‌ی اجازه
    this.saveConsent("yes");
    this.muted.value = false;
    this.denied.value = false;
    this.failed = false;
    this.unlock();
    this.refreshAsk();
    this.ensure();
    // نوبتِ من نیست (مافیا): اجازه‌ی مرورگر را همین حالا بگیر، نه وسطِ نوبت.
    if (!this.canSpeak.value && this.turn.value) void this.warmMic();
  }

  /** فقط پنجره‌ی اجازه‌ی مرورگر — میکروفون همان لحظه بسته می‌شود. */
  private async warmMic(): Promise<void> {
    try {
      const s = await navigator.mediaDevices.getUserMedia({ audio: true });
      for (const t of s.getTracks()) t.stop();
    } catch (e) {
      const name = (e as { name?: string } | null)?.name ?? "";
      if (name === "NotAllowedError" || name === "SecurityError") this.denied.value = true;
    }
  }

  /** «فعلاً نه» — پاسخ می‌مانَد؛ دکمه بی‌صدا نشان می‌دهد و لمسش دوباره می‌پرسد. */
  decline(): void {
    this.saveConsent("no");
    this.refreshAsk();
    this.ensure();
  }

  /** پرسش را بی پاسخ بست — سرِ همین میز دوباره نپرس، چیزی هم نمان. */
  skipAsk(): void {
    this.skipped = true;
    this.refreshAsk();
  }

  /** تنها کنترلِ میز. بی‌اجازه/ردشده؟ همان accept (دکمه پیش از این توضیح را نشان می‌دهد). */
  toggleMute(): void {
    if (this.phase.value !== "live" || !this.canSpeak.value) return;
    this.unlock();
    if (this.consent.value !== "yes" || this.denied.value) { this.accept(); return; }
    this.muted.value = !this.muted.value;
    this.failed = false;
    this.ensure();
  }

  /** میکروفون *باید* باز باشد؟ */
  private wantMic(): boolean {
    return this.phase.value === "live" && !this.listenOnly && this.canSpeak.value && this.consent.value === "yes"
      && !this.muted.value && !this.denied.value;
  }

  private refreshAsk(): void {
    // سرِ میزِ مافیا حرف‌زدن تا نوبت بسته است؛ باز هم همان اول بپرس.
    this.ask.value = this.phase.value === "live" && !this.listenOnly && (this.canSpeak.value || this.turn.value !== "")
      && this.consent.value === "unknown" && !this.skipped;
  }

  /** پشتِ سرِ هم، نه هم‌زمان: لمس‌های تند نباید میکروفون را باز جا بگذارند. */
  private ensure(): void {
    this.busy = this.busy.then(async () => {
      const room = this.room;
      if (!room) { this.micOn.value = false; return; }
      const want = this.wantMic() && !this.failed;
      if (want !== room.localParticipant.isMicrophoneEnabled) {
        if (want) this.opening.value = true;
        try {
          await room.localParticipant.setMicrophoneEnabled(want);
        } catch (e) {
          console.warn("voice: microphone", e);
          // NotAllowedError: کاربر/مرورگر رد کرد، یا صفحه https نیست (فقط https و localhost).
          const name = (e as { name?: string } | null)?.name ?? "";
          if (name === "NotAllowedError" || name === "SecurityError") this.denied.value = true;
          else this.failed = true;
        } finally {
          this.opening.value = false;
        }
      }
      if (this.room !== room) return;
      this.micOn.value = room.localParticipant.isMicrophoneEnabled;
      this.roster(room);
      // تا کار تمام شد خواسته عوض شد (بی‌صدا/باصدا، پنل)؟ یک دورِ دیگر — فقط همان وقت، تا حلقه نشود.
      if ((this.wantMic() && !this.failed) !== want) this.ensure();
    }).catch((e) => {
      // زنجیر هرگز رد‌شده نمانَد، وگرنه هیچ ensure ِ بعدی اجرا نمی‌شود.
      console.warn("voice: ensure", e);
      this.opening.value = false;
    });
  }

  /** قفلِ پخشِ مرورگر را باز کن (فقط از رویدادِ کاربر کار می‌کند). */
  private unlock(): void {
    const room = this.room;
    if (!room || !this.blocked.value) return;
    void room.startAudio().then(() => { this.blocked.value = !room.canPlaybackAudio; }, () => {});
  }

  /** اجازه‌ی میکروفونِ مرورگر: ردشده از پیش؟ و اگر کاربر از تنظیماتِ سایت بازش کرد، همان لحظه باز. */
  private async watchPermission(): Promise<void> {
    try {
      const st = await navigator.permissions?.query({ name: "microphone" as PermissionName });
      if (!st) return;
      const apply = () => {
        const was = this.denied.value;
        this.denied.value = st.state === "denied";
        if (was && !this.denied.value) { this.failed = false; this.ensure(); }
      };
      apply();
      st.addEventListener("change", apply);
    } catch { /* فایرفاکسِ قدیمی «microphone» را نمی‌شناسد — از خطای getUserMedia می‌فهمیم */ }
  }

  private saveConsent(c: VoiceConsent): void {
    this.consent.value = c;
    try { localStorage.setItem(CONSENT_KEY, c); } catch { /* حالتِ خصوصی */ }
  }

  leave(): void {
    this.gen++;
    this.table = "";
    this.listenOnly = false;
    this.skipped = false;
    this.failed = false;
    for (const el of this.els.values()) el.remove();
    this.els.clear();
    const room = this.room;
    this.room = null;
    if (room) void room.disconnect();
    this.micOn.value = false;
    this.opening.value = false;
    this.canSpeak.value = false;
    this.speaking.value = EMPTY;
    this.members.value = EMPTY;
    this.silenced.value = EMPTY;
    this.blocked.value = false;
    this.phase.value = "off";
    this.ask.value = false;
  }
}

/**
 * میزی که در آن نشسته‌ام یا تماشایش می‌کنم — تماشاچی هم صدای میز را می‌شنود، فقط می‌شنود.
 * تخته‌ای‌ها (منچ، مارپله، نرد، دوز، دومینو) هم — تا اکتبر ۲۰۲۶ از این فهرست جا مانده بودند و
 * دکمه سرِ میزشان هرگز پیدا نمی‌شد، با این‌که سرور بلیتشان را می‌داد.
 */
function atTable(): { id: string; watching: boolean } {
  for (const t of [client.table.value, client.cbTable.value, client.hkTable.value, client.shTable.value, client.board.value, client.mafia.value]) {
    if (t) return { id: t.tableId, watching: t.watching || !!client.watching.value };
  }
  return { id: "", watching: false };
}

const CONSENT_KEY = "bazivan.voice.consent";
function readConsent(): VoiceConsent {
  try {
    const v = localStorage.getItem(CONSENT_KEY);
    return v === "yes" || v === "no" ? v : "unknown";
  } catch { return "unknown"; }
}

export const voice = new Voice();
blocked.subscribe(() => voice.applyBlocks());
